import React, { lazy, Suspense, FC, useCallback, useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom'
import { Spin } from 'antd'
import i18n from 'i18next'

import { useAuthStore } from './store/auth-store'
import { useActivationStore } from './store/activation-store'
import { useAuthService, useCompanyService } from './api/use-service'
import AuthLayout from './layout/AuthLayout'
import MainLayout from './layout/MainLayout'

const LoginPage = lazy(() => import('./pages/LoginPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const ForgotPage = lazy(() => import('./pages/ForgotPage'))
const RecoveryPage = lazy(() => import('./pages/RecoveryPage'))
const VerificationPage = lazy(() => import('./pages/VerificationPage'))
const CallbackPage = lazy(() => import('./pages/CallbackPage'))
const HomePage = lazy(() => import('./pages/HomePage'))
const CompanyPage = lazy(() => import('./pages/CompanyPage'))
const EmployeeRiskDashboardPage = lazy(() => import('./pages/EmployeeRiskDashboardPage'))
const CreateDiagnosticPage = lazy(() => import('./pages/CreateDiagnosticPage'))
const CreateYamlDiagnosticPage = lazy(() => import('./pages/CreateYamlDiagnosticPage'))
const DiagnosticsPage = lazy(() => import('./pages/DiagnosticsPage'))
const TrainingWatchPage = lazy(() => import('./pages/TrainingWatchPage'))
const DiagnosticConversationPage = lazy(() => import('./pages/DiagnosticConversationPage'))
const InterviewConversationPage = lazy(() => import('./pages/InterviewConversationPage'))
const LandingPage = lazy(() => import('./pages/LandingPage'))
const SchedulesPage = lazy(() => import('./pages/SchedulesPage'))
const ComplianceReportBuilderPage = lazy(() => import('./pages/ComplianceReportBuilderPage'))
const PhishingPage = lazy(() => import('./pages/PhishingPage'))
const ControlAssessmentPage = lazy(() => import('./pages/ControlAssessmentPage'))
const ControlFindingsPage = lazy(() => import('./pages/ControlFindingsPage'))
const AtlasOnboardingPage = lazy(() => import('./pages/AtlasOnboardingPage'))
const DevAiPhishingPreviewPage = lazy(() => import('./pages/DevAiPhishingPreviewPage'))
const DevAtlasDebugPage = lazy(() => import('./pages/DevAtlasDebugPage'))

export enum routes {
  LANDING = '/landing',
  HOME = '/home',
  LOGIN = '/signin',
  REGISTER = '/signup',
  FORGOT = '/forgot-password',
  RECOVERY = '/password-recovery',
  VERIFICATION = '/verification',
  AUTH_CALLBACK = '/callback',
  COMPANY = '/company',
  DASHBOARD = '/dashboard',

  CREATE_DIAGNOSTIC = '/new-diagnostic',
  CREATE_YAML_DIAGNOSTIC = '/new-scenario-diagnostic',
  DIAGNOSTICS = '/scenarios',
  SCHEDULES = '/schedules',
  COMPLIANCE_REPORT_BUILDER = '/compliance-reports/builder/:reportId?',
  PHISHING = '/simulations',
  CONTROL_ASSESSMENT = '/controls',
  CONTROL_FINDINGS = '/controls/:assessmentId/findings/:assignmentId',
  // Dev-only superadmin tools. Not exposed in any nav; backend enforces
  // SuperAdminMiddleware on the simulate endpoint.
  DEV_AI_PHISHING_PREVIEW = '/dev/ai-phishing-preview',
  DEV_ATLAS_DEBUG = '/dev/atlas-debug',
}

export const onboardingRoutes = [
  { path: routes.LOGIN, label: 'routes.login', element: LoginPage },
  { path: routes.REGISTER, label: 'routes.register', element: RegisterPage },
  { path: routes.FORGOT, label: 'routes.forgot', element: ForgotPage },
  { path: routes.RECOVERY, label: 'routes.recovery', element: RecoveryPage },
  { path: routes.VERIFICATION, label: 'routes.verification', element: VerificationPage },
  { path: routes.AUTH_CALLBACK, label: 'routes.auth_callback', element: CallbackPage },
]
export const mainRoutes = [
  { path: routes.HOME, label: 'Home', element: HomePage },
  { path: routes.DASHBOARD, label: 'Dashboard', element: EmployeeRiskDashboardPage },
  { path: routes.CREATE_DIAGNOSTIC, label: 'Create Diagnostic (Expert)', element: CreateDiagnosticPage },
  { path: routes.CREATE_YAML_DIAGNOSTIC, label: 'Create Diagnostic (YAML)', element: CreateYamlDiagnosticPage },
  { path: routes.DIAGNOSTICS, label: 'Diagnostics', element: DiagnosticsPage },
  { path: routes.SCHEDULES, label: 'Schedules', element: SchedulesPage },
  { path: routes.COMPLIANCE_REPORT_BUILDER, label: 'Report Builder', element: ComplianceReportBuilderPage },
  { path: routes.PHISHING, label: 'Phishing', element: PhishingPage },
  { path: routes.CONTROL_ASSESSMENT, label: 'Audit', element: ControlAssessmentPage },
  { path: routes.CONTROL_FINDINGS, label: 'Audit Findings', element: ControlFindingsPage },
  // Dev-only superadmin tool: included ONLY when VITE_APP_NODE_ENV is NOT
  // 'production'. Same env var the codebase already uses for environment
  // gating (see utils/analytics.ts:5). In prod the route is absent and the
  // URL resolves to the standard not-found fallback.
  ...(import.meta.env.VITE_APP_NODE_ENV !== 'production'
    ? [
        { path: routes.DEV_AI_PHISHING_PREVIEW, label: 'Dev — AI Phishing Preview', element: DevAiPhishingPreviewPage },
        { path: routes.DEV_ATLAS_DEBUG, label: 'Dev — Atlas Normalization Debug', element: DevAtlasDebugPage },
      ]
    : []),
]

// Config routes: available to any activated logged-in user.
export const configRoutes = [{ path: routes.COMPANY, label: 'routes.company', element: CompanyPage }]

const RequireAuth: FC<{ children: JSX.Element; isLogged: boolean; isLoading: boolean }> = ({
  children,
  isLogged,
  isLoading,
}) => {
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLogged) {
      navigate({ pathname: routes.LOGIN })
    }
  }, [isLogged, navigate])

  if (isLoading) {
    return <Spin className="flex h-screen w-full items-center justify-center" />
  }

  return children
}

const RequireNonAuth: FC<{ children: JSX.Element; isLogged: boolean; isLoading: boolean }> = ({
  children,
  isLogged,
  isLoading,
}) => {
  const navigate = useNavigate()

  useEffect(() => {
    if (isLogged) {
      navigate({ pathname: routes.HOME })
    }
  }, [isLogged, navigate])

  if (isLoading) {
    return <Spin className="flex h-screen w-full items-center justify-center" />
  }

  return children
}

// Full-screen placeholder for logged-in users whose company hasn't been
// flipped to `activated:true` yet. No self-serve activation flow in MVP —
// an operator does it via mongo.
const PendingActivationScreen: FC = () => (
  <div className="flex h-screen w-full flex-col items-center justify-center gap-3 px-6 text-center">
    <h1 className="text-2xl font-semibold text-text-primary">{i18n.t('activation.pending_title')}</h1>
    <p className="max-w-md text-sm text-text-secondary">{i18n.t('activation.pending_body')}</p>
  </div>
)

// Guard that requires the company to be flipped to `activated:true`.
const RequireActivated: FC<{ children: JSX.Element }> = ({ children }) => {
  const { activated, loading } = useActivationStore()

  if (loading || activated === undefined) {
    return <Spin className="flex h-screen w-full items-center justify-center" />
  }

  if (!activated) {
    return <PendingActivationScreen />
  }

  return children
}

export const Router: FC = () => {
  const { isLogged, login, user } = useAuthStore()
  const { activated, setActivated, setLoading: setActivationLoading } = useActivationStore()
  const { checkAuthentication } = useAuthService()
  const { getCompany } = useCompanyService()
  const initialized = useRef(false)

  const checkAuth = useCallback(async () => {
    const { data, error } = await checkAuthentication.invoke()
    if (!error && data) {
      login({ ...data })
    }
    return data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkAuthentication.invoke])

  // Read the company's `activated` flag from the getCompany endpoint. The
  // endpoint is intentionally NOT gated by ActivationMiddleware so we can
  // decide whether to show the pending-activation screen.
  const fetchActivation = useCallback(
    async (companyId: string) => {
      setActivationLoading(true)
      const { data, error } = await getCompany.invoke({ pathParam: companyId })
      if (!error && data?.company) {
        setActivated(Boolean(data.company.activated))
      } else {
        setActivated(false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [getCompany.invoke],
  )

  useEffect(() => {
    try {
      checkAuth()
    } finally {
      initialized.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isLogged && user?.companyIds?.[0] && activated === undefined) {
      fetchActivation(user.companyIds[0])
    }
  }, [isLogged, user?.companyIds, activated, fetchActivation])

  if (checkAuthentication.isLoading || !initialized.current) {
    return <Spin className="flex h-screen w-full items-center justify-center" />
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/training/w/:token"
          element={
            <Suspense fallback={<Spin className="flex h-screen w-full items-center justify-center" />}>
              <TrainingWatchPage />
            </Suspense>
          }
        />
        <Route
          path="/diagnostic/w/:token"
          element={
            <Suspense fallback={<Spin className="flex h-screen w-full items-center justify-center" />}>
              <DiagnosticConversationPage />
            </Suspense>
          }
        />
        <Route
          path="/interview/w/:token"
          element={
            <Suspense fallback={<Spin className="flex h-screen w-full items-center justify-center" />}>
              <InterviewConversationPage />
            </Suspense>
          }
        />
        {/* Public Atlas onboarding via token */}
        <Route
          path="/atlas/o/:token"
          element={
            <Suspense fallback={<Spin className="flex h-screen w-full items-center justify-center" />}>
              <AtlasOnboardingPage />
            </Suspense>
          }
        />
        {/* Landing page - public route (no auth required) */}
        <Route
          path="/landing"
          element={
            <Suspense fallback={<Spin className="flex h-screen w-full items-center justify-center" />}>
              <LandingPage />
            </Suspense>
          }
        />
        <Route
          path="/"
          element={
            isLogged ? (
              <RequireAuth isLogged={isLogged} isLoading={checkAuthentication.isLoading}>
                <RequireActivated>
                  <MainLayout isConfiguration={false} withTitle={false}>
                    <Suspense fallback={<Spin className="flex w-full flex-1 items-center justify-center py-3" />}>
                      <HomePage />
                    </Suspense>
                  </MainLayout>
                </RequireActivated>
              </RequireAuth>
            ) : (
              <Suspense fallback={<Spin className="flex h-screen w-full items-center justify-center" />}>
                <LandingPage />
              </Suspense>
            )
          }
        />
        {
          // Onboarding layout
        }
        <Route
          element={
            <RequireNonAuth isLogged={isLogged} isLoading={checkAuthentication.isLoading}>
              <AuthLayout
                children={
                  <Suspense fallback={<Spin className="flex w-full flex-1 items-center justify-center py-3" />}>
                    <Outlet />
                  </Suspense>
                }
              />
            </RequireNonAuth>
          }
        >
          {onboardingRoutes.map(({ path, element: Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
        </Route>
        {
          // Main layout - requires activated company
        }
        <Route
          element={
            <RequireAuth isLogged={isLogged} isLoading={checkAuthentication.isLoading}>
              <RequireActivated>
                <MainLayout
                  isConfiguration={false}
                  children={
                    <Suspense fallback={<Spin className="flex w-full flex-1 items-center justify-center py-3" />}>
                      <Outlet />
                    </Suspense>
                  }
                />
              </RequireActivated>
            </RequireAuth>
          }
        >
          {mainRoutes.map(({ path, element: Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
        </Route>
        {
          // Configuration layout - requires activated company
        }
        <Route
          element={
            <RequireAuth isLogged={isLogged} isLoading={checkAuthentication.isLoading}>
              <RequireActivated>
                <MainLayout
                  isConfiguration
                  children={
                    <Suspense fallback={<Spin className="flex w-full flex-1 items-center justify-center py-3" />}>
                      <Outlet />
                    </Suspense>
                  }
                />
              </RequireActivated>
            </RequireAuth>
          }
        >
          {configRoutes.map(({ path, element: Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
        </Route>
        <Route path="*" element={<Navigate to={isLogged ? routes.HOME : routes.LOGIN} replace />} />
      </Routes>
    </BrowserRouter>
  )
}
